import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { environment } from '../../../environments/environment';

/** Esquema custom para reentrar a la app desde el callback web (Custom Tab / SFSafari). */
export const CLAVE_UNICA_APP_SCHEME = 'indapsat';

/**
 * Flujo OAuth Clave Única vía broker institucional (claveunica.php).
 * En nativo usa returnUrl de environment (satapp) y Browser; en web, origin + navegación completa.
 */
@Injectable({
  providedIn: 'root'
})
export class ClaveUnicaAuthFlowService {
  private readonly router = inject(Router);

  private listenersNativosRegistrados = false;
  private flujoActivo = false;
  private reentradaRecibida = false;
  private onCancelar: (() => void) | null = null;

  /**
   * URL de callback que se envía al broker.
   * En nativo añade `from_app=1` para que la PWA en satapp redirija a `indapsat://` si App Links no disparan.
   */
  obtenerReturnUrl(): string {
    if (Capacitor.isNativePlatform()) {
      const u = new URL(environment.claveUnica.returnUrl);
      u.searchParams.set('from_app', '1');
      return u.toString();
    }
    return `${window.location.origin}/callback`;
  }

  construirUrlAutorizacion(): string {
    const qs = new URLSearchParams({
      client_id: environment.claveUnica.clientId,
      return_url: this.obtenerReturnUrl()
    });
    return `${environment.claveUnica.baseUrl}?${qs.toString()}`;
  }

  /**
   * Inicia login con Clave Única.
   * @param onCancelar Se invoca si el usuario cierra el navegador in-app sin completar (solo nativo).
   */
  async iniciarFlujo(onCancelar: () => void): Promise<void> {
    const url = this.construirUrlAutorizacion();

    if (!Capacitor.isNativePlatform()) {
      window.location.assign(url);
      return;
    }

    this.onCancelar = onCancelar;
    this.flujoActivo = true;
    this.reentradaRecibida = false;
    this.registrarListenersNativos();

    await Browser.open({ url });
  }

  /** Llamado desde main.ts cuando App Links / custom scheme entrega la URL de callback. */
  marcarReentradaRecibida(): void {
    this.reentradaRecibida = true;
    this.flujoActivo = false;
    this.onCancelar = null;
    void Browser.close();
  }

  private registrarListenersNativos(): void {
    if (this.listenersNativosRegistrados) {
      return;
    }
    this.listenersNativosRegistrados = true;

    void Browser.addListener('browserFinished', () => {
      if (this.reentradaRecibida) {
        this.reentradaRecibida = false;
        return;
      }
      if (!this.flujoActivo) {
        return;
      }
      const path = this.router.url.split('?')[0];
      if (path === '/callback' || path.endsWith('/callback')) {
        this.flujoActivo = false;
        return;
      }
      this.invocarCancelacion();
    });

  }

  private invocarCancelacion(): void {
    this.flujoActivo = false;
    const cb = this.onCancelar;
    this.onCancelar = null;
    cb?.();
  }
}

/**
 * Parsea URL de reentrada (App Link / Universal Link / indapsat://) hacia ruta Angular /callback.
 */
export function rutaCallbackDesdeUrlExterna(url: string): string | null {
  try {
    const u = new URL(url);
    const esCallback =
      u.pathname.includes('callback') ||
      u.hostname === 'callback';
    if (!esCallback && !u.searchParams.has('jwt')) {
      return null;
    }
    const jwt = u.searchParams.get('jwt');
    if (jwt) {
      return `/callback?jwt=${encodeURIComponent(jwt)}`;
    }
    const path = u.pathname && u.pathname !== '/' ? u.pathname : '/callback';
    return `${path}${u.search}`;
  } catch {
    return null;
  }
}
